/**
 * Electricity prices by geography (California, counties, cities).
 * Source: electricity_prices_by_geography, electricity_utility_crosswalk (Supabase).
 */

import { supabaseClient } from './supabase';

export interface ElectricityPriceRow {
  geofips: string;
  location_name: string | null;
  county: string | null;
  geography_type: string;
  year: number;
  sector: string;
  avg_price_per_kwh: number;
  /** EIA-861 utility name for this row when known (year-varying: IOU before CCA start, CCA after). */
  utility_name?: string | null;
  /** Energy component $/kWh (CCA) when CCA-served; null for bundled. */
  energy_price_per_kwh?: number | null;
  /** Delivery component $/kWh (IOU) when CCA-served; null for bundled. */
  delivery_price_per_kwh?: number | null;
}

export interface ElectricityPricesByGeographyResult {
  location: ElectricityPriceRow[];
  california: ElectricityPriceRow[];
  utilityName: string | null;
  /** IOU name from crosswalk (for Delivery label when CCA+IOU stacked). */
  eia861Iou?: string | null;
  /** CCA name from crosswalk (for Energy label when CCA+IOU stacked). */
  eia861Cca?: string | null;
}

/**
 * Fetch electricity prices for a location and California by geofips.
 * Also returns the EIA-861 utility name for the location (if mapped in crosswalk).
 */
export async function fetchElectricityPricesByGeography(geofips: string | number): Promise<ElectricityPricesByGeographyResult> {
  const raw = geofips !== null && geofips !== undefined && String(geofips).trim() !== '' ? geofips : null;
  if (raw === null) {
    const caRes = await supabaseClient.from('electricity_prices_by_geography').select('geofips,location_name,county,geography_type,year,sector,avg_price_per_kwh,utility_name,energy_price_per_kwh,delivery_price_per_kwh').eq('geofips', '06').order('year');
    return {
      location: [],
      california: (caRes.data || []) as ElectricityPriceRow[],
      utilityName: null,
      eia861Iou: null,
      eia861Cca: null
    };
  }
  const g = String(raw).trim().replace(/^0+/, '') || '0';
  // Match DB format: state = 2 digits (06); county/city = as stored in population (no leading zero padding)
  const geofipsNorm = g.length <= 2 ? g.padStart(2, '0') : g;

  const selectCols = 'geofips,location_name,county,geography_type,year,sector,avg_price_per_kwh,utility_name,energy_price_per_kwh,delivery_price_per_kwh';
  let locRes = await supabaseClient.from('electricity_prices_by_geography').select(selectCols).eq('geofips', geofipsNorm).order('year');
  // If no rows, try numeric geofips in case DB column type differs (debug)
  if (!locRes.error && (!locRes.data || locRes.data.length === 0) && geofipsNorm !== '06') {
    const numGeofips = typeof geofips === 'number' ? geofips : parseInt(geofipsNorm, 10);
    if (!Number.isNaN(numGeofips)) {
      const alt = await supabaseClient.from('electricity_prices_by_geography').select(selectCols).eq('geofips', numGeofips).order('year');
      if (!alt.error && alt.data && alt.data.length > 0) {
        locRes = alt;
      }
    }
  }
  const [caRes, cwRes] = await Promise.all([
    supabaseClient.from('electricity_prices_by_geography').select(selectCols).eq('geofips', '06').order('year'),
    supabaseClient.from('electricity_utility_crosswalk').select('eia861_utility_name,eia861_iou,eia861_cca').eq('geofips', geofipsNorm).maybeSingle()
  ]);

  if (locRes.error) throw new Error(locRes.error.message || 'Failed to fetch location electricity prices');
  if (caRes.error) throw new Error(caRes.error.message || 'Failed to fetch California electricity prices');

  const location = (locRes.data || []) as ElectricityPriceRow[];
  const california = (caRes.data || []) as ElectricityPriceRow[];
  let utilityName = cwRes.data?.eia861_utility_name ?? null;
  let eia861Iou = cwRes.data?.eia861_iou ?? null;
  let eia861Cca = cwRes.data?.eia861_cca ?? null;
  // If crosswalk missed with string, try numeric (debug)
  if (!cwRes.data && !cwRes.error && geofipsNorm !== '06') {
    const numGeofips = typeof geofips === 'number' ? geofips : parseInt(geofipsNorm, 10);
    if (!Number.isNaN(numGeofips)) {
      const cwAlt = await supabaseClient.from('electricity_utility_crosswalk').select('eia861_utility_name,eia861_iou,eia861_cca').eq('geofips', numGeofips).maybeSingle();
      if (cwAlt.data) {
        utilityName = cwAlt.data.eia861_utility_name ?? null;
        eia861Iou = cwAlt.data.eia861_iou ?? null;
        eia861Cca = cwAlt.data.eia861_cca ?? null;
      }
    }
  }

  return { location, california, utilityName, eia861Iou, eia861Cca };
}

/** Latest year's average $/kWh across sectors (for key metrics). */
export function getLatestAveragePrice(rows: ElectricityPriceRow[]): number | null {
  if (!rows.length) return null;
  const years = Array.from(new Set(rows.map(r => r.year))).sort((a, b) => b - a);
  const latestYear = years[0];
  const byYear = rows.filter(r => r.year === latestYear);
  if (!byYear.length) return null;
  return byYear.reduce((s, r) => s + r.avg_price_per_kwh, 0) / byYear.length;
}

/**
 * Building electricity price $/kWh for cost models.
 * When resFractionKwh is provided (0–1), uses weighted average: res * resFractionKwh + com * (1 - resFractionKwh).
 * Otherwise uses simple average of residential and commercial when both available.
 */
export function getBuildingElectricityPricePerKwh(
  residential: number | null,
  commercial: number | null,
  resFractionKwh?: number | null
): number | null {
  if (residential != null && commercial != null) {
    let price: number;
    if (resFractionKwh != null && resFractionKwh >= 0 && resFractionKwh <= 1) {
      price = residential * resFractionKwh + commercial * (1 - resFractionKwh);
    } else {
      price = (residential + commercial) / 2;
    }
    return Math.round(price * 1000) / 1000;
  }
  return residential ?? commercial ?? null;
}

/**
 * Fetch latest-year residential and commercial electricity consumption (kWh) for a geography.
 * Used to weight building electricity price by res/com share in most recent year.
 */
export async function fetchLatestResCommercialKwhByGeofips(geofips: string | number): Promise<{
  resKwh: number;
  comKwh: number;
  year: number;
} | null> {
  const raw = String(geofips).trim().replace(/^0+/, '') || null;
  if (!raw) return null;
  try {
    const [resRes, comRes] = await Promise.all([
      supabaseClient.from('elec_res_format').select('year, total_kwh').eq('geofips', raw).eq('sector', 'Residential').order('year', { ascending: false }),
      supabaseClient.from('elec_com_format').select('year, total_kwh').eq('geofips', raw).eq('sector', 'Commercial').order('year', { ascending: false }),
    ]);
    const resRows = (resRes.data || []) as { year: number; total_kwh: number }[];
    const comRows = (comRes.data || []) as { year: number; total_kwh: number }[];
    if (!resRows.length || !comRows.length) return null;
    const resByYear = new Map<number, number>();
    for (const r of resRows) {
      const y = r.year;
      resByYear.set(y, (resByYear.get(y) ?? 0) + (Number(r.total_kwh) || 0));
    }
    const comByYear = new Map<number, number>();
    for (const r of comRows) {
      const y = r.year;
      comByYear.set(y, (comByYear.get(y) ?? 0) + (Number(r.total_kwh) || 0));
    }
    const allYears = Array.from(new Set([...Array.from(resByYear.keys()), ...Array.from(comByYear.keys())])).sort((a, b) => b - a);
    for (const year of allYears) {
      const resKwh = resByYear.get(year) ?? 0;
      const comKwh = comByYear.get(year) ?? 0;
      if (resKwh > 0 || comKwh > 0) {
        return { resKwh, comKwh, year };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Get latest year's residential and commercial prices for a location (for Policy tool). */
export async function fetchLatestElectricityPricesForLocation(geofips: string | number): Promise<{
  residential: number | null;
  commercial: number | null;
  average: number | null;
  /** Building cost assumption: weighted average by res/com kWh share in most recent year; falls back to (res+com)/2. */
  buildingPricePerKwh: number | null;
  utilityName: string | null;
  year: number | null;
}> {
  const [{ location, utilityName }, kwhLatest] = await Promise.all([
    fetchElectricityPricesByGeography(geofips),
    fetchLatestResCommercialKwhByGeofips(geofips),
  ]);
  const sectors = location.filter(r => ['Residential', 'Commercial'].includes(r.sector));
  const years = Array.from(new Set(sectors.map(r => r.year))).sort((a, b) => b - a);
  const latestYear = years[0] ?? null;
  if (latestYear == null) {
    return { residential: null, commercial: null, average: null, buildingPricePerKwh: null, utilityName, year: null };
  }
  const bySector = sectors.filter(r => r.year === latestYear);
  const res = bySector.find(r => r.sector === 'Residential')?.avg_price_per_kwh ?? null;
  const com = bySector.find(r => r.sector === 'Commercial')?.avg_price_per_kwh ?? null;
  const allSectors = location.filter(r => r.year === latestYear);
  const avg = allSectors.length > 0
    ? allSectors.reduce((s, r) => s + r.avg_price_per_kwh, 0) / allSectors.length
    : null;
  const totalKwh = kwhLatest ? kwhLatest.resKwh + kwhLatest.comKwh : 0;
  const resFractionKwh = totalKwh > 0 && kwhLatest ? kwhLatest.resKwh / totalKwh : undefined;
  const buildingPricePerKwh = getBuildingElectricityPricePerKwh(res, com, resFractionKwh);
  return {
    residential: res ?? null,
    commercial: com ?? null,
    average: avg ?? buildingPricePerKwh ?? (res ?? com),
    buildingPricePerKwh,
    utilityName,
    year: latestYear
  };
}
